import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useRouter } from "next/router";
import Auto from "/components/common/auto";
import clsx from "clsx";
import Icon from "/components/common/icon";
import { safeLocalStorage } from "~/utils/safeStorage";
import { getFilterUrl, getType } from "../../../../utils";
import styles from "./index.module.scss";

export default function Selector({
  sectionPageUrl,
  isBaltex = false,
  isLanding = false,
}) {
  const router = useRouter();
  const { autos } = useSelector((state) => state.user);
  const { marks, models, types } = useSelector((state) => state.models);
  const { params } = useSelector((state) => state.filter);
  const autosList = Array.isArray(autos) ? autos : [];
  const marksMap = marks ?? {};
  const modelsMap = models ?? {};
  const typesMap = types ?? {};

  const resetAll = () => {
    router.push(getFilterUrl(sectionPageUrl));
  };

  const changeMark = (value) => {
    if (!value) {
      return;
    }
    safeLocalStorage.setItem("mark_", value);
    safeLocalStorage.setItem("model_", "");
    safeLocalStorage.setItem("type_", "");
    router.push(getFilterUrl(sectionPageUrl, params, { marka: value }));
  };

  const changeModel = (value) => {
    if (!value) {
      return;
    }
    safeLocalStorage.setItem("model_", value);
    safeLocalStorage.setItem("type_", "");
    router.push(getFilterUrl(sectionPageUrl, params, { model: value }));
  };

  const changeTypes = (value) => {
    if (!value) {
      return;
    }
    safeLocalStorage.setItem("type_", value);
    safeLocalStorage.setItem(
      "filterUrl",
      `/filter/marka-${params.marka?.[0]}/model-${params.model?.[0]}/kuzov-${value}`,
    );
    router.push(getFilterUrl(sectionPageUrl, params, { kuzov: value }));
  };

  const [isSelectOther, setIsSelectOther] = useState(false);

  const selectedAuto = useMemo(() => {
    return (
      autosList &&
      (() => {
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < autosList.length; i++) {
          const auto = autosList[i];
          if (
            auto.MARK_CODE === params.marka?.[0] &&
            auto.MODEL_CODE === params.model?.[0] &&
            auto.CODE === params.kuzov?.[0]
          ) {
            return auto;
          }
        }
        return undefined;
      })()
    );
  }, [autosList, params]);

  useEffect(() => {
    const somethingSelected =
      params.marka?.[0] || params.model?.[0] || params.kuzov?.[0];
    if (somethingSelected && !selectedAuto) {
      setIsSelectOther(true);
    }
  }, [params, selectedAuto]);

  const isShowMyAutos = useMemo(() => {
    if (autosList.length > 0 && !isSelectOther) {
      return true;
    }
    return false;
  }, [autosList, isSelectOther]);

  const markKeys = Object.keys(marksMap).sort();
  const modelsKeys = Object.keys(modelsMap).sort();

  return markKeys.length > 0 ? (
    <>
      <div className={styles.MobileSelector}>
        {!isLanding && (
          <div>
            <span>Марка</span>
            <Icon width={7} height={15} id="arrow" fill={"#fff"} />
          </div>
        )}
        <div>
          <span>Модель</span>
          <Icon width={7} height={15} id="arrow" fill={"#fff"} />
        </div>
        <div>
          <span>Кузов</span>
          <Icon width={7} height={15} id="arrow" fill={"#fff"} />
        </div>
      </div>
      <div className={clsx(isBaltex && styles.SelectorBaltex, styles.Selector)}>
        <div className={styles.Title}>
          Выберите автомобиль
          {params?.marka && (
            <span
              className={styles.Close}
              onClick={() => {
                safeLocalStorage.setItem("filterUrl", "");
                router.push(sectionPageUrl);
              }}
            >
              <Icon id="close" fill={"#fff"} width={22} height={22} />
            </span>
          )}
        </div>

        {isShowMyAutos && (
          <>
            <div className={styles.Cars}>
              {Array.isArray(autos) &&
                autos.map((auto) => (
                  <div
                    key={auto.ID}
                    className={clsx(
                      styles.Auto,
                      selectedAuto?.ID === auto.ID && styles.AutoSelected,
                    )}
                    onClick={() => {
                      safeLocalStorage.setItem(
                        "filterUrl",
                        `/filter/${auto.URL}`,
                      );
                      router.push(`${sectionPageUrl}filter/${auto.URL}`);
                    }}
                  >
                    <Auto auto={auto} />
                  </div>
                ))}
            </div>
            <div
              className={styles.SelectOther}
              onClick={() => setIsSelectOther(true)}
            >
              Выбрать другой авто
              <Icon width={20} height={15} id="arrowcatalog" />
            </div>
          </>
        )}

        {!isShowMyAutos && (
          <>
            {!isLanding && (
              <select
                value={params?.marka}
                onChange={(event) => changeMark(event.target.value)}
                placeholder="Марка"
              >
                <option key={0} value={""}>
                  Марка
                </option>
                {markKeys.map((markCode) => (
                  <option key={markCode} value={markCode}>
                    {marksMap[markCode].NAME}
                  </option>
                ))}
              </select>
            )}
            {modelsKeys.length > 0 && (
              <select
                value={params?.model}
                onChange={(event) => changeModel(event.target.value)}
              >
                <option key={0} value={""}>
                  Модель
                </option>
                {modelsKeys.map((modelCode) => (
                  <option key={modelCode} value={modelCode}>
                    {modelsMap[modelCode]?.NAME}
                  </option>
                ))}
              </select>
            )}
            {Object.keys(typesMap).length > 0 && (
              <select
                value={params?.kuzov}
                onChange={(event) => changeTypes(event.target.value)}
              >
                <option key={0} value={""}>
                  Кузов
                </option>
                {Object.keys(typesMap)
                  .map((typeCode) => getType(typesMap[typeCode].NAME, typeCode))
                  .sort((a, b) => a.sort - b.sort)
                  .map((item) => (
                    <option key={item.code} value={item.code}>
                      {item.name} {item.gen}
                    </option>
                  ))}
              </select>
            )}
            {params?.marka > 0 && (
              <div onClick={resetAll} aria-label="Сбросить все фильтры">
                Сбросить все
              </div>
            )}
            {autosList.length > 0 && (
              <div
                className={styles.SelectMy}
                onClick={() => setIsSelectOther(false)}
                aria-label="Вернуться к вашим авто"
              >
                <Icon width={20} height={15} id="arrowcatalog" />
                Назад к вашим авто
              </div>
            )}
          </>
        )}
      </div>
    </>
  ) : null;
}
